import {
  all, call, fork, spawn, put, select, takeEvery, takeLatest, take, race, delay
} from 'redux-saga/effects';
import merge from 'lodash.merge';
import { v4 as v4uuid } from 'uuid';
import { PageActionTypes } from './types';
import { push } from '../../utils/customRouter';
import SessionHelper from '../../utils/sessionHelper';
import DeviceHelper from '../../utils/deviceHelper';
import {
  pageRequest,
  pageSuccess,
  mergeStyleSuccess,
  clearForms,
  formValuesSuccess,
  resetFormValuesSuccess,
  updateVariableSuccess,
  uncheckDependentRadioSuccess,
  formValuesRequest,
  activateLocalShimmerSuccess,
  actionSuccess,
  mergeStyleRequest,
  submitFormRequest,
  actionRequest,
} from './actions';
import { store, history } from '../../configureStore';
import { configRequest, emptyConfigRequest, updateEndpointParams } from '../config/actions';
import apiRequest from '../../utils/apiReq';
import { normalizeApiResponse, normalizeResponseBody, normalizeSessionData } from '../../utils/normalizeAppData';
import { getFdsRequestHeader, toCacheableBody } from '../../utils/designSystem';
import { isFrameMode } from '../../utils/req';
import { 
  setItem as setLocalCacheRaw, 
  getItem as getLocalCache,
  getItemRegexp as getLocalCacheRegexp,
  removeItem as removeFromLocalCache
 } from '../../utils/indexedDbHelper';
import { 
  recursiveUpdateById, 
  reccursivelyUncheckRadioItemsLegacy,
  getDeviceInfo, 
  parseFormValues, 
  replacePlaceholdersWithVariables,
  substitutePathParams,
  matchesPattern,
  formShimmerCacheKey,
  transformShimmerCache,
  mergeLocalVariablesIntoGlobal,
  parseOldFormat,
  parseNewFormat,
  applyDocumentLanguage,
  updateDataWithModeUpdate,
  sessionDataTransformer,
  executeFunction,
  splitFunctionsByTiming,
  runPreFunctions,
  reportPreCancelled,
  handleOpenUrl,
 } from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import { getGlobalVariables } from '../../utils/globalVariableHelper';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import {
  NavContext,
  getNavContext,
  pushNavState,
  replaceNavState,
} from '../../utils/navigationContext';
/**
 * FE-4577 — every screen-cache write goes through here so the design system is
 * stripped first. Wrapped at the import rather than at each of the nine call
 * sites below: the bodies are now merged with the whole branch's classes and
 * templates, and one missed site would embed a full duplicate copy.
 */
const setLocalCache = (key: string, body: any) => setLocalCacheRaw(key, toCacheableBody(body));

// A runtime style-merge chunk (e.g. an on_click writing active_state: "success"
// onto a node) must NEVER clobber a variable BINDING such as
// "{{v.decision_select_state}}" with a concrete literal. The page tree is
// re-resolved from this same store slice on every render, so once the binding
// string is overwritten the node is permanently unlinked from its variable and
// code-driven states (e.g. `disabled`) stop applying. See TNBEWEB-2001.
const ACTIVE_STATE_BINDING_RE = /\{\{.*?\}\}/;

const isBindingString = (value: unknown): boolean =>
  typeof value === 'string' && ACTIVE_STATE_BINDING_RE.test(value);

// Preserve a variable-bound active_state; otherwise take the incoming literal,
// falling back to the existing value (original `incoming || existing` behavior).
const mergeActiveState = (incoming: unknown, existing: unknown): unknown =>
  isBindingString(existing) ? existing : incoming || existing;

// If `binding` is exactly "{{ v.<path> }}" (a single writable LOCAL variable
// reference — no surrounding text), return the dotted path, else null. This is
// the case we can safely redirect a literal into: instead of overwriting the
// binding we write the literal to the bound variable and KEEP the binding, so
// the node — and every other node bound to the same variable — stays reactive.
// Composite strings ("x-{{v.a}}"), row scope ({{this.*}}) and global/bare refs
// are excluded; callers fall back to preserving the binding without a redirect.
const extractRedirectableVarPath = (binding: unknown): string | null => {
  if (typeof binding !== 'string') return null;
  const match = binding.match(/^\s*\{\{\s*(v\.[^{}]+?)\s*\}\}\s*$/);
  return match ? match[1] : null;
};

 function styleMerger(page: any, updates: any) {
  const inArr = Array.isArray(page) ? page : [page];
    const updatesMap = updates.reduce((map, update) => {
    map[update.object_id] = update;
    return map;
  }, {} as Record<string, any>);
  const updatedComponents = recursiveUpdateById(inArr, updatesMap, (item) => {
    const update = Object.keys(updatesMap).find((objId) => 
      objId === item.id || (matchesPattern(objId, item.id) && updatesMap[objId]?.object_type === item.component)
    );

    if (update) {
      return {
        ...item,
        active_state: mergeActiveState(updatesMap[update]?.active_state, item.active_state),
        style: merge({}, item.style, updatesMap[update]?.style),
      };
    }
    return item;
  });
  return updatedComponents;
}

// Opens a hidden <input type="file"> and resolves with the user's selection.
// Resolves with an empty array when the picker is dismissed without a choice.
const pickFiles = ({ accept, multiple }: { accept?: string[]; multiple?: boolean }): Promise<File[]> =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (multiple) input.multiple = true;
    if (accept && accept.length) input.accept = accept.join(',');
    input.style.display = 'none';

    let settled = false;
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      resolve(files);
      if (input.parentNode) input.parentNode.removeChild(input);
      window.removeEventListener('focus', onFocus);
    };

    input.addEventListener('change', () => {
      const files = input.files ? Array.from(input.files) : [];
      finish(files);
    });

    // Detect cancel: browsers don't fire a cancel event reliably, but focus
    // returns to the window when the dialog closes. Check the input shortly
    // after — if it still has no files, treat it as a cancel.
    const onFocus = () => {
      setTimeout(() => {
        if (!settled && (!input.files || input.files.length === 0)) {
          finish([]);
        }
      }, 300);
    };
    window.addEventListener('focus', onFocus, { once: true });

    document.body.appendChild(input);
    input.click();
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handlePageRequest(action: any) {
  // When `features.processes` is on, the deployment runs in actions-mode
  // and the legacy `request` flow is replaced by `action` chunks — skip
  // entirely so migrated schemes don't double-fire legacy requests.
  const processes = yield select((state: any) => state.config?.data?.features?.processes);
  if (processes === true) return;

  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { location, silent, componentId, onSuccess, onError } = payload || {};
  const { pathname: pathnameDraft, search } = location || {};

  const pathname = (pathnameDraft === '/') ? '/' : pathnameDraft.replace(/\/$/, '');

  // Correct the route if it ends with a trailing slash without triggering a location change
  if (pathnameDraft.length > 1 && pathnameDraft.endsWith('/')) {
    const correctedPathname = pathnameDraft.slice(0, -1);
    const newUrl = search ? `${correctedPathname}${search}` : correctedPathname;
    window.history.replaceState(null, '', newUrl);
  }

  try {
    const isFrameMode = search ? new URLSearchParams(search).has('frame') : false;
    const breakLoadPages = ['/not_found', '/frame/internal'];
    if (pathname && !isFrameMode && !breakLoadPages.includes(pathname)) {
      const page = (pathname === '/' || pathname === '') ? '/' : pathname.replace('/', '');
      yield put(clearForms());

      const cacheKey = formShimmerCacheKey({ command: `${page}${search?.length ? search : ''}`, action: undefined, component_id: componentId, screen_id: screenId });
      const [key, value] = cacheKey ? yield call(getLocalCacheRegexp, cacheKey) : [null, null];

      if (!/\{\{[^}]+\}\}/.test(key) && !silent) {
        try {
          const currentPath = window.location.pathname === '/' ? '/' : window.location.pathname.replace(/\/$/, '');
          const currentSearch = window.location.search;
          const navigateTo = search ? `${pathname}${search}` : pathname;
                
          if (currentPath !== pathname ||
              (search && currentSearch !== search)) {
            if ((window.location.pathname + window.location.search) !== navigateTo) {
              yield put(push(navigateTo));
            }
          }
        } catch (navError) {
          console.error('Navigation failed:', navError);
          // Continue execution even if navigation fails
        }
      }

     if (value && !silent) {
        // Cache entries are written from a normalized body, but an entry from an
        // older build (or a future envelope change) may not be — normalizing is
        // idempotent, so it is free insurance.
        const cached = normalizeResponseBody(value);
        const { page: pp, settings, variableData, screenId: screenIdParsed } = cached.sections ? parseOldFormat(cached) : parseNewFormat(cached);
        let mergedData;
        if (localShimmer?.length) {
          mergedData = styleMerger(pp, localShimmer);
        }
        yield put(pageSuccess({
          data: mergedData || pp, 
          settings, 
          session_data: {
            type: 'indexedDb',
            localShimmer: !!localShimmer?.length,
            hash: v4uuid(),
          }, 
          variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
          screenId: screenIdParsed,
         }));
      } else if (localShimmer?.length) {
        yield put(activateLocalShimmerSuccess());
      }

      const rawGetResponse = yield call(
        apiRequest,
        'post',
        '/get',
        {
          command: `${page}${search?.length ? search : ''}`,
          skip_cache: silent ? true : !!value,
          screen_id: screenId,
          active_route: `${window.location.pathname}${window.location.search || ''}`,
          component_id: componentId,
          device_info: getDeviceInfo(),
          session_id: SessionHelper.getOrCreateSessionId(),
        },
      );
      // 0.22.x drops the `data` wrapper and sinks mode/live_updates/localization
      // into `screen.settings` — normalize so everything below (mode branch,
      // parseNewFormat, the cache writes) keeps reading one shape.
      const { code, data, session_data } = normalizeApiResponse(rawGetResponse);
      if (code !== 200 || !data) {
        if (onError?.length) {
          yield call(processEventChunks, onError, { success: false, error: 'Invalid response', code, data }, componentId);
        }
        return;
      }
      const { page: pp, settings, variableData, screenId: screenIdParsed } = data.sections ? parseOldFormat(data) : parseNewFormat(data);

      // Handle new update mode scheme
      let finalPageData = pp;
      let finalVariableData = variableData;
      let shouldUpdateCache = false;
      
      if (data.mode === 'update') {
        const currentPageData = yield select((state) => state.page.data.page);
        const currentVariableData = yield select((state) => state.page.variableData);
        
        const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
          updateDataWithModeUpdate(currentPageData, data, currentVariableData);
        
        finalPageData = updatedPage;
        finalVariableData = updatedVariableData;
        shouldUpdateCache = cacheFlag;
      }

      yield put(pageSuccess({ 
        data: finalPageData, 
        settings, 
        session_data: {
          ...sessionDataTransformer(session_data),
          localShimmer: false,
          // `skip-` after a cached render (it already reset, TNBEWEB-1484) and on a silent
          // refresh — FE-4866: that reset now reaches `.pageWrap`, the whole page.
          hash: (value || silent) ? `skip-${v4uuid()}` : v4uuid(),
        }, 
        variableData: mergeLocalVariablesIntoGlobal(finalVariableData, settings?.variables),
        screenId: screenIdParsed,
       }));

       const { status, transformed } = transformShimmerCache(data?.screen?.sections || []);

      // Handle caching based on mode and settings
      if (data.mode === 'update') {
        // For update mode, save the final transformed data
        if (shouldUpdateCache) {
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: finalPageData,
            },
            variable_data: finalVariableData,
          });
        }
      } else if (settings?.command && settings?.cache) {
          setLocalCache(settings.command, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });

          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });
        } else if (status && transformed && !silent && settings?.cache) {
          // Update response with transformed sections
          // Save
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: transformed,
            },
            variable_data: variableData,
          });
        } else if (!componentId && !screenId && !silent && settings?.cache) {
          setLocalCache(cacheKey, {
            ...data,
            screen: {
              ...data.screen,
              sections: data?.screen?.sections,
            },
            variable_data: variableData,
          });
        }
      
      // else if (!silent) {
      //   removeFromLocalCache(cacheKey);
      // }

          // Handle settings.command URL update
      if (settings?.command) {
        const newCommand = settings.command.replace(/^\//, '').replace(/\/$/, '');
        const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;
        
        if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
          // yield put(push(`/${newCommand}`));
          if (window.location.pathname + window.location.search !== `/${newCommand}`) {
            window.history.pushState(null, '', `/${newCommand}`);
          }
        }
      }

      // Parse form values
      const formValues = parseFormValues(finalPageData);
      yield put(resetFormValuesSuccess({ data: formValues }));
      if (onSuccess?.length) {
        yield call(processEventChunks, onSuccess, { success: true, data, code }, componentId);
      }
    }
  } catch (error) {
    if (onError?.length) {
      yield call(processEventChunks, onError, { success: false, error: error instanceof Error ? error.message : String(error) }, componentId);
    }
  }
}

function* handleSetFormValuesRequest(action: any) {
  const { payload } = action || {};
  const { field } = payload || {};
  const { id, value } = field || {};

  yield put(formValuesSuccess({ data: { [id]: value } }));
}

// Resolve a {{$response.key.nested}} placeholder against a response context.
// Returns undefined when the variable name is not a $response.* reference or
// when the path cannot be found — the caller then falls back to form values.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resolveResponseContextValue = (variableName: string, responseContext: any): any => {
  if (!responseContext || typeof variableName !== 'string') return undefined;
  const trimmed = variableName.trim();
  if (!trimmed.startsWith('$response')) return undefined;

  if (trimmed === '$response') return responseContext;

  const path = trimmed.slice('$response.'.length);
  if (!path) return responseContext;

  return path.split('.').reduce((acc: any, segment: string) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[segment];
  }, responseContext);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// Helper function to convert value to specified type
const convertToType = (value: any, type?: string): any => {
  if (!type || value === null || value === undefined) return value;
  
  switch (type.toLowerCase()) {
    case 'string':
      return String(value);
    case 'number':
      const num = Number(value);
      return isNaN(num) ? String(value) : num;
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
      }
      return Boolean(value);
    case 'array':
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch {
          return [value];
        }
      }
      return [value];
    case 'object':
      if (typeof value === 'object') return value;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'object' && parsed !== null ? parsed : { value };
        } catch {
          return { value };
        }
      }
      return { value };
    case 'file':
      // Marker type for FormData assembly — value is passed through as-is
      // (File | File[] | undefined). Actual file selection happens in the
      // upload action handler; this branch simply preserves the value.
      return value;
    default:
      return String(value);
  }
};

function* sendAllFormValues(action: any) {
  // When `features.processes` is on, the deployment runs in actions-mode
  // and the legacy `request` flow is replaced by `action` chunks — skip
  // entirely so migrated schemes don't double-fire legacy requests.
  const processes = yield select((state: any) => state.config?.data?.features?.processes);
  if (processes === true) return;

  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { value, delay: del, componentId, block, silent, onSuccess, onError } = payload || {};
  // Stop processing if block is true
  if (block) {
    return;
  }
  const page = yield select((state) => state.page);
  const { settings, variableData, formValues } = page || {};
  const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
  const replacedFormValue = replacePlaceholdersWithVariables(formValues, mergedVariables);

  yield delay(del|| 0);

  const cacheKey = formShimmerCacheKey({ command: undefined, action: value, component_id: componentId, screen_id: screenId });
  const [key2, value2] = cacheKey ? yield call(getLocalCache, cacheKey) : [null, null];

if (value2 && !silent) {
    const cached2 = normalizeResponseBody(value2);
    const { page: pp, settings, variableData, screenId: screenIdParsed } = cached2.sections ? parseOldFormat(cached2) : parseNewFormat(cached2);
            let mergedData;
        if (localShimmer?.length) {
          mergedData = styleMerger(pp, localShimmer);
        }
    yield put(pageSuccess({ 
      data: mergedData || pp,
      settings,
      session_data: {
        localShimmer: !!localShimmer?.length,
        type: 'indexedDb',
        hash: `skip-${v4uuid()}`,
      },
      variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
      screenId: screenIdParsed,
     }));
  }  else if (localShimmer?.length) {
      yield put(activateLocalShimmerSuccess());
  }

  try {
    yield put(clearForms());
    const rawGetResponse = yield call(
      apiRequest,
      'post',
      '/get',
      {
        action: value,
        screen_id: screenId,
        skip_cache: silent ? true : !!value,
        active_route: `${window.location.pathname}${window.location.search || ''}`,
        component_id: componentId,
        device_info: getDeviceInfo(),
        session_id: SessionHelper.getOrCreateSessionId(),
        ...replacedFormValue,
      },
    );
    const { code, data, session_data } = normalizeApiResponse(rawGetResponse);
    if (code !== 200 || !data) return;
    const { page: pp, settings, variableData, screenId: screenIdParsed } = data.sections ? parseOldFormat(data) : parseNewFormat(data);
    
    // Handle new update mode scheme
    let finalPageData = pp;
    let finalVariableData = variableData;
    let shouldUpdateCache = false;
    
    if (data.mode === 'update') {
      const currentPageData = yield select((state) => state.page.data.page);
      const currentVariableData = yield select((state) => state.page.variableData);
      
      const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } = 
        updateDataWithModeUpdate(currentPageData, data, currentVariableData);
      
      finalPageData = updatedPage;
      finalVariableData = updatedVariableData;
      shouldUpdateCache = cacheFlag;
    }
    
    yield put(pageSuccess({ 
      data: finalPageData, 
      settings, 
      session_data: {
        ...sessionDataTransformer(session_data),
        localShimmer: false,
        hash: `skip-${v4uuid()}`,
      }, 
      variableData: mergeLocalVariablesIntoGlobal(finalVariableData, settings?.variables),
      screenId: screenIdParsed,
     }));

     const { status, transformed } = transformShimmerCache(data?.screen?.sections || []);

    // Handle caching based on mode and settings
    if (data.mode === 'update') {
      // For update mode, save the final transformed data
      if (shouldUpdateCache) {
        setLocalCache(cacheKey, {
          ...data,
          screen: {
            ...data.screen,
            sections: finalPageData,
          },
          variable_data: finalVariableData,
        });
      }
    } else if (status && transformed && !silent && settings?.cache) {
        setLocalCache(cacheKey, {
          ...data,
          screen: {
            ...data.screen,
            sections: transformed,
          },
          variable_data: variableData,
        });
      } else if (!silent) {
        if (cacheKey) removeFromLocalCache(cacheKey);
      } 

    // Handle settings.command URL update
    if (settings?.command) {
      const newCommand = settings.command.replace(/^\//, '').replace(/\/$/, '');
      const currentPath = window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;
      
      if (!/\{\{[^}]+\}\}/.test(newCommand) && newCommand !== currentPath) {
        // yield put(push(`/${newCommand}`));
        if (window.location.pathname + window.location.search !== `/${newCommand}`) {
          window.history.pushState(null, '', `/${newCommand}`);
        }
      }
    }

    // Parse form values
    const formValues = parseFormValues(finalPageData);
    yield put(resetFormValuesSuccess({ data: formValues }));
    if (onSuccess?.length) {
      yield call(processEventChunks, onSuccess, { success: true, data, code }, componentId);
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error('Error in sendAllFormValues:', error.message);
    }
    if (onError?.length) {
      yield call(processEventChunks, onError, { success: false, error: error instanceof Error ? error.message : String(error) }, componentId);
    }
  }
}

function* updateVariableRequest(action: any) {
  const { payload } = action || {};
  const { variable, value } = payload || {};
  const keys = variable.split('.');
  const result = keys.reduceRight((acc, key) => ({
    [key]: acc
  }), value);

  // $setVariable semantics: arrays REPLACE the stored value (no index-merge stale tail).
  yield put(updateVariableSuccess({ data: result, replaceArrays: true }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleLocationChange(action: any) {
  const { location, isFirstRendering, state, action: navAction } = action.payload;
  if (isFirstRendering === true) {
    // Check if config is already initialized from init script
    const configState = yield select((state: any) => state.config);
    const isConfigFromInitScript = configState?.initializedFromInitScript === true;

    if (isConfigFromInitScript) {
      // yield put(pageRequest({ ...action.payload }));
    } else if (isFrameMode()) {
      // View mode: the editor is the source of truth for the schema — it posts its
      // (possibly unsaved) working copy over the iframe handshake. `configRequest`
      // chains a `pageRequest` for the PERSISTED screen, whose PAGE_SUCCESS lands a
      // few hundred ms later and overwrites the posted schema — and, because the
      // reducer resets `variableData` wholesale, it also wipes everything the screen's
      // `on_launch` handlers wrote. Those handlers don't re-run (Container latches
      // `launchExecutedRef`), so the preview renders correctly for a moment and then
      // permanently breaks. Fetch the config WITHOUT the page fetch here; navigation
      // after the handshake still goes through the normal saga/API flow.
      yield put(emptyConfigRequest(location));
    } else {
      yield put(configRequest(location));
    }
    return;
  }

  // Back/forward — replay the exact request that put us on this URL. On
  // pushState(null) entries (state === null) we intentionally make no
  // backend request, per TNBEWEB-1905 acceptance criteria.
  if (navAction === 'POP') {
    const ctx = getNavContext(state);
    if (ctx) {
      try {
        yield call(executeNavigationRequest, ctx, {
          silent: false,
          updateHistory: false,
        });
      } catch (error) {
        console.error('Navigation replay failed:', error);
      }
    }
    return;
  }

  // Legacy PUSH/REPLACE: keep the old command-based path alive for components
  // that still dispatch pageRequest directly. processes:true navigation goes
  // through handleActionRequest and does not land here.
  yield put(pageRequest({ ...action.payload }));
}

function* handleMergeStyle(action: any) {
  const { payload, meta } = action || {};
  const { context, index } = meta || {};
  const updates = Array.isArray(payload) ? payload : [payload];
  const { page } = yield select((state) => state.page.data);
  const styleChunks = yield select((state) => state.page.styleChunks);
  const { indexes, refs } = styleChunks || {};

  // Helper function to extract context values and generate ref keys
  const generateRefKey = (objectId: string, context: any) => {
    if (!context || !objectId.includes('{{')) return null;
    
    let refKey = objectId;
    
    // Match patterns like {{this.id}}, {{demo}}, {{demo.key}}
    const matches = objectId.match(/\{\{([^}]+)\}\}/g);
    
    if (matches) {
      matches.forEach(match => {
        const path = match.slice(2, -2); // Remove {{ and }}
        let value;
        
        // Handle {{this.}} patterns - extract from current context
        if (path.startsWith('this.')) {
          const contextPath = path.substring(5); // Remove 'this.'
          value = getNestedValue(context, contextPath);
        } else if (path.startsWith('this[')) {
          const contextPath = path.substring(4); // Remove 'this'
          value = getNestedValue(context, contextPath);
        } else {
          // Handle other patterns like {{demo}}, {{demo.key}}
          value = getNestedValue(context, path);
        }
        
        if (value !== undefined) {
          refKey = refKey.replace(match, value);
        }
      });
    }
    
    return refKey !== objectId ? refKey : null;
  };

  // Helper function to get nested value from object
  const getNestedValue = (obj: any, path: string) => {
    if (!path || obj === null || obj === undefined) return undefined;
    
    // Parse the path to handle both dot notation and bracket notation
    const parsePath = (pathStr: string): string[] => {
      const keys: string[] = [];
      let current = '';
      let inBrackets = false;
      let bracketDepth = 0;
      
      for (let i = 0; i < pathStr.length; i++) {
        const char = pathStr[i];
        
        if (char === '[') {
          if (inBrackets) {
            bracketDepth++;
            current += char;
          } else {
            if (current) {
              keys.push(current);
              current = '';
            }
            inBrackets = true;
          }
        } else if (char === ']') {
          if (bracketDepth > 0) {
            bracketDepth--;
            current += char;
          } else {
            inBrackets = false;
            if (current) {
              // Handle nested brackets and evaluate the content
              let key = current;
              
              // If the key contains quotes, remove them
              if ((key.startsWith('"') && key.endsWith('"')) || 
                  (key.startsWith("'") && key.endsWith("'"))) {
                key = key.slice(1, -1);
              }
              
              // If the key is a number, keep it as is
              // If it contains nested brackets or dots, it might be a complex expression
              keys.push(key);
              current = '';
            }
          }
        } else if (char === '.' && !inBrackets) {
          if (current) {
            keys.push(current);
            current = '';
          }
        } else {
          current += char;
        }
      }
      
      if (current) {
        keys.push(current);
      }
      
      return keys;
    };
    
    const keys = parsePath(path);
    
    return keys.reduce((current, key) => {
      if (current === null || current === undefined) return undefined;
      
      // Handle numeric keys for arrays
      if (!isNaN(Number(key))) {
        const numKey = Number(key);
        return Array.isArray(current) ? current[numKey] : current[key];
      }
      
      // Handle regular object keys
      return current[key];
    }, obj);
  };

  // Generate all ref updates at top level FIRST
  updates.forEach(update => {
    const refKey = generateRefKey(update.object_id, context);
    
    if (refKey && update.style) {
      if (!refs[refKey]) {
        refs[refKey] = {};
      }
      // Save refs with type and update structure
      refs[refKey] = merge({}, refs[refKey], {
        type: update.object_type,
        update: update?.style || {},
      });
    }
  });

  const updatesMap = updates.reduce((map, update) => {
    map[update.object_id] = update;
    return map;
  }, {} as Record<string, any>);

  const inArr = Array.isArray(page) ? page : [page];
  // TNBEWEB-2001: when a style chunk carries a literal active_state for a node
  // whose active_state is bound to a single writable variable ({{v.*}}), we do
  // NOT bake the literal onto the node (that would unlink it from the variable
  // and break code-driven states like `disabled`). Instead we keep the binding
  // and redirect the literal into the bound variable — collected here (keyed by
  // path so the last write wins), dispatched after the tree merge below.
  const variableRedirects = new Map<string, unknown>();
  const updatedComponents = recursiveUpdateById(inArr, updatesMap, (item, insideTable) => {
    const update = Object.keys(updatesMap).find((objId) =>
      objId === item.id || (matchesPattern(objId, item.id) && updatesMap[objId]?.object_type === item.component)
    );

    if (update) {
      const updateData = updatesMap[update];

      if (insideTable && typeof index === 'number') {
        if (!indexes[item.id]) {
          indexes[item.id] = {};
        }
        indexes[item.id][index] = merge({}, indexes[item.id][index], updateData?.style);
        return item;
      }

      const incomingActiveState = updateData?.active_state;
      const boundVarPath = extractRedirectableVarPath(item.active_state);
      if (
        incomingActiveState != null &&
        incomingActiveState !== '' &&
        !isBindingString(incomingActiveState) &&
        boundVarPath
      ) {
        // Redirect the literal into the bound variable; keep item.active_state
        // as the "{{v.*}}" binding (mergeActiveState preserves it below).
        variableRedirects.set(boundVarPath, incomingActiveState);
      }
      return {
        ...item,
        active_state: mergeActiveState(incomingActiveState, item.active_state),
        style: merge({}, item.style, updateData?.style),
      };
    }
    return item;
  });

  yield put(mergeStyleSuccess({
    data: updatedComponents,
    styleChunks: { indexes, refs } // Include updated refs
  }));

  // Apply the collected active_state → variable redirects. Reuses the variable
  // write saga so every node bound to each variable re-resolves on next render.
  if (variableRedirects.size) {
    yield all(
      Array.from(variableRedirects, ([variable, value]) =>
        call(updateVariableRequest, { payload: { variable, value } })),
    );
  }
}

function* handleUncheck(action: any) {
  const { payload } = action || {};
  const { name, excludeId, originalName } = payload || {};
  const { page } = yield select((state) => state.page.data);
  const hasVariables = /\{\{.*?\}\}/.test(originalName);
  if (hasVariables) {
    // Radios whose name is templated ({{…}}) are intentionally NOT unchecked here: the
    // DOM-scan implementation this branch was written for was never enabled and has been
    // removed. Restoring it means reinstating a name->ids resolution that works on the
    // rendered DOM, since a templated name cannot be matched in the persisted tree.
  } else {
    const { items, changedIds } = reccursivelyUncheckRadioItemsLegacy(page, name, excludeId);

    yield put(uncheckDependentRadioSuccess({ data: items }));

    for (const item of changedIds) {
      yield put(formValuesRequest({ field: { id: item, value: false } }));
    }

  }

}

function* watchLocationChange() {
  yield takeEvery(PageActionTypes.LOCATION_CHANGE, handleLocationChange);
}

// Helper function to merge payloads by object_id while preserving context/index
const mergePayloadsByContext = (batches: any, newPayload: any, newMeta: any) => {
  const contextKey = `${newMeta?.context ? JSON.stringify(newMeta.context) : 'null'}_${newMeta?.index ?? 'null'}`;
  
  if (!batches[contextKey]) {
    batches[contextKey] = {
      meta: newMeta,
      payloads: []
    };
  }
  
  const currentBatch = batches[contextKey];
  const payloadArray = Array.isArray(newPayload) ? newPayload : [newPayload];
  
  payloadArray.forEach((payload: any) => {
    const existingIndex = currentBatch.payloads.findIndex((item: any) => item.object_id === payload.object_id);
    if (existingIndex !== -1) {
      // Merge styles for existing object_id
      currentBatch.payloads[existingIndex] = {
        ...currentBatch.payloads[existingIndex],
        ...payload,
        style: merge({}, currentBatch.payloads[existingIndex].style, payload.style),
      };
    } else {
      currentBatch.payloads.push(payload);
    }
  });
};

// Batch MERGE_STYLE_REQUEST actions within 45ms, preserving context and index
function* mergeStyleBatcher() {
  let batches: Record<string, { meta: any; payloads: any[] }> = {}; // Group by context + index
  
  while (true) {
    // Wait for the first MERGE_STYLE_REQUEST
    const firstAction = yield take(PageActionTypes.MERGE_STYLE_REQUEST);
    mergePayloadsByContext(batches, firstAction.payload, firstAction.meta);

    // Now, for 45ms, collect any more MERGE_STYLE_REQUESTs
    while (true) {
      const { nextAction, timeout } = yield race({
        nextAction: take(PageActionTypes.MERGE_STYLE_REQUEST),
        timeout: delay(50),
      });
      
      if (nextAction) {
        mergePayloadsByContext(batches, nextAction.payload, nextAction.meta);
      } else {
        
        for (const [contextKey, batch] of Object.entries(batches)) {
          yield call(handleMergeStyle, { 
            payload: batch.payloads, 
            meta: batch.meta 
          });
        }
        
        batches = {};
        break;
      }
    }
  }
}

// FE-4677: page init runs the whole scheme through replacePlaceholdersWithVariables, which bakes
// every `{{…}}` of an event chunk long before the click; the pre-substitution template survives
// as `<key>Original` (`toOriginal`, `valueOriginal`). `{{this.*}}` is already resolved inside it:
// the component runs its whole `events` subtree through replacePlaceholdersWithVariablesV2.
const eventTemplate = (original: unknown, baked: any) => (
  typeof original === 'string' && /\{\{[^}]+\}\}/.test(original) ? original : baked
);

// FE-4677: page init resolved templates against a bag that ALSO held the globals
// (`getGlobalVariables()`, exposed as `{{c.*}}`), but an event-time bag has none. Re-resolving a
// recovered template without them would turn a `{{c.*}}` that used to bake correctly into an
// unresolved token. Globals live under the `c` key, so they cannot collide with page variables.
const withGlobalVariables = (variables: any) => merge({}, variables, getGlobalVariables() || {});

// Event-time bag for URL templates: page variables (+ `$response` when chained) + globals.
function* selectUrlVariables(responseContext?: any) {
  const variableData = yield select((state: any) => state.page.variableData);
  const settings = yield select((state: any) => state.page.settings);
  const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
  return withGlobalVariables(responseContext
    ? merge({}, mergedVariables, { $response: responseContext })
    : mergedVariables);
}

// TNBEWEB-2022: a legacy "Open url" request chained from on_success / on_error navigates exactly
// like a component's own one (handleOpenUrl: new-tab flag, delay, SPA push), with its URL
// resolved at event time — after the level's pre functions wrote their variables.
function* openChainedUrl(
  template: any,
  delaySeconds: number | undefined,
  openInNewTab: boolean | undefined,
  responseContext: any,
) {
  const variables = yield call(selectUrlVariables, responseContext);
  const url = typeof template === 'string' ? replacePlaceholdersWithVariables(template, variables) : template;
  // '' would reload the page (location.href = '').
  if (typeof url !== 'string' || !url) return;
  // An explicit flag wins; only a scheme that never stored one keeps this path's old
  // default (an http URL opened a new tab).
  const newTab = openInNewTab ?? url.startsWith('http');
  // In processes mode `history.push` only changes the URL — the page request it triggers is
  // skipped (handlePageRequest) — so a relative path keeps its full reload there.
  const processes = yield select((state: any) => state.config?.data?.features?.processes);
  handleOpenUrl(url, delaySeconds, newTab, processes === true ? undefined : history);
}

// The same chunk order as a component's own event (runFunctionsWithTiming → runEffects):
// variables → `during` functions → `pre` functions (a throw / rejection skips everything after
// them and raises no on_error) → styles → actions → requests.
function* runEventChunks(chunks: any[], responseContext: any, ownerId?: string) {
  // Variables are assigned before every other chunk in the event (first in the
  // stack), so subsequent style/action/function chunks see the new values.
  const variableUpdates = chunks.filter((u: any) => isVariablesChunk(u));
  if (variableUpdates.length) {
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: state.page?.variableData,
      localVariables: state.page?.settings?.variables,
      thisContext: responseContext,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      yield put(updateVariableSuccess({ data }));
    }
  }

  const styleUpdates = chunks.filter((u: any) => u && 'style' in u);
  const actionUpdates = chunks.filter((u: any) => u && 'action' in u);
  const requestUpdates = chunks.filter((u: any) => u && 'request' in u);
  const { pre, during } = splitFunctionsByTiming(chunks.filter((u: any) => u && 'function' in u));

  // TNBEWEB-1992: shared handler scope. `directWrites` keeps this path's synchronous $set
  // (so a later function-chunk in the same on_success can $get an earlier chunk's write).
  // No repeatable row in the response context → `this` stays undefined here.
  const internalFns = buildInternalFns({
    store,
    response: responseContext,
    directWrites: true,
    owner: ownerId ? { id: ownerId } : undefined,
  });

  during.forEach((update) => {
    executeFunction(update.function, undefined, internalFns);
  });

  if (pre.length) {
    try {
      const pending = runPreFunctions(pre, internalFns);
      // Only a real Promise is yielded: even an already-resolved one would cost a microtask.
      if (pending) yield pending;
    } catch (err) {
      reportPreCancelled(internalFns, err);
      return;
    }
    internalFns.onPrePassed?.();
  }

  if (styleUpdates.length) {
    yield put(mergeStyleRequest(styleUpdates));
  }

  for (const update of actionUpdates) {
    yield put(actionRequest({ actionUpdate: update, responseContext }));
  }

  for (const update of requestUpdates) {
    const { method, value, valueOriginal, silent, config } = update?.request || {};
    const { delay: reqDelay } = config || {};
    if (method === 'command') {
      yield put(pageRequest({ location: { pathname: value }, silent }));
    } else if (method === 'action') {
      yield put(submitFormRequest({ value, delay: reqDelay, silent }));
    } else if (method === 'open-url') {
      yield call(openChainedUrl, eventTemplate(valueOriginal, value), reqDelay, silent, responseContext);
    }
  }
}

// TNBEWEB-2022: on_success / on_error run DETACHED from the worker that raised them. That worker
// is a takeLatest task (ACTION_REQUEST / PAGE_REQUEST / SUBMIT_FORM_REQUEST), and a chained
// action / command / submit dispatches the very pattern it was started by: takeLatest cancelled
// the running worker, and with it every chunk still queued after that dispatch — the function in
// "on_success: [Open url, function]" never ran. Detached, an exception inside the chain also can
// no longer kill the root saga. Every caller invokes this as its final step.
function* processEventChunks(chunks: any[], responseContext?: any, ownerId?: string) {
  if (!chunks?.length) return;
  yield spawn(runEventChunks, chunks, responseContext, ownerId);
}

// FE-4866 — the address a response asks the browser to show: its `settings.command`
// resolved, without the leading/trailing slash. null when there is no command or a
// placeholder is left unresolved — such a response never changes the URL.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const resolveCommandAddress = (command: unknown, variables: any): string | null => {
  if (typeof command !== 'string' || !command) return null;
  const resolved = replacePlaceholdersWithVariables(command, variables) as string;
  const address = resolved.replace(/^\//, '').replace(/\/$/, '');
  return /\{\{[^}]+\}\}/.test(address) ? null : address;
};

// The address the browser shows now, in the shape resolveCommandAddress returns.
const currentAddress = () => window.location.pathname.replace(/^\//, '').replace(/\/$/, '') + window.location.search;

// Runs a NavContext snapshot — same logic whether this is an initial action
// request or a back/forward replay from window.history.state. Callers pass
// `updateHistory: true` to save the new entry via push/replaceState; replays
// pass `false` because popstate already placed us on the right URL.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* executeNavigationRequest(
  snapshot: NavContext,
  opts: {
    silent?: boolean;
    onSuccess?: any[];
    onError?: any[];
    responseContext?: any;
    localShimmer?: any;
    variablesForCommand?: any;
    updateHistory: boolean;
  }
) {
  const { url, method, headers: snapshotHeaders, body, componentId, cacheActionUrl, screenId: snapshotScreenId } = snapshot;
  const { silent, onSuccess, onError, responseContext, localShimmer, variablesForCommand, updateHistory } = opts;

  // FE-4866 — the page scroller (`.pageWrap`) and scrollable containers go back to the top
  // when a PAGE_SUCCESS carries a fresh hash; a `skip-` hash leaves them where they are. A
  // navigation gets exactly ONE fresh hash, on the first render of the new screen: the cached
  // copy when there is one, otherwise the server's. The server render that follows a cached
  // one (TNBEWEB-1484), `mode: update`, `silent` requests and a response for the screen and
  // address already shown all keep `skip-`. A back/forward replay (`updateHistory: false`)
  // always counts — popstate has already moved the URL, so the address test can't see it.
  const shownScreenId = yield select((state: any) => state.page.screenId);
  const isNavigationTo = (address: string | null, nextScreenId: unknown): boolean => (
    !updateHistory
    || (nextScreenId != null && String(nextScreenId) !== String(shownScreenId))
    || (address !== null && address !== currentAddress())
  );
  let scrollResetDone = false;

  const cacheKey = formShimmerCacheKey({ command: undefined, action: cacheActionUrl || url, component_id: componentId, screen_id: snapshotScreenId });
  const [, value2] = cacheKey ? yield call(getLocalCache, cacheKey) : [null, null];

  if (value2 && !silent) {
    const cachedNav = normalizeResponseBody(value2);
    const { page: pp, settings: cachedSettings, variableData: cachedVariableData, screenId: screenIdParsed } = cachedNav.sections ? parseOldFormat(cachedNav) : parseNewFormat(cachedNav);
    let mergedData;
    if (localShimmer?.length) {
      mergedData = styleMerger(pp, localShimmer);
    }
    const cachedAddress = resolveCommandAddress(
      cachedSettings?.command,
      merge({}, variablesForCommand || {}, cachedVariableData || {}, (cachedSettings?.variables || {})),
    );
    scrollResetDone = cachedNav.mode !== 'update' && isNavigationTo(cachedAddress, screenIdParsed);
    yield put(pageSuccess({
      data: mergedData || pp,
      settings: cachedSettings,
      session_data: {
        localShimmer: !!localShimmer?.length,
        type: 'indexedDb',
        hash: scrollResetDone ? v4uuid() : `skip-${v4uuid()}`,
      },
      variableData: mergeLocalVariablesIntoGlobal(cachedVariableData, cachedSettings?.variables),
      screenId: screenIdParsed,
    }));
  } else if (localShimmer?.length) {
    yield put(activateLocalShimmerSuccess());
  }

  yield put(clearForms());

  // Fsc is derived from the live cache state at call time, not from the snapshot —
  // a replay might hit cache even if the original call did not, or vice versa.
  //
  // FE-4577 — Fds is derived here for the same reason, and it matters more: the
  // snapshot in `history.state` can be minutes old, so a back/forward replay
  // would claim an inventory we have long since moved past. The backend
  // self-heals from a stale hash (it answers `r=1` and re-delivers), so this is
  // not a correctness problem — but it would throw away the entire benefit on
  // exactly the navigation that should be cheapest.
  //
  // Gated on same-origin: unlike Fsc, this header describes the branch's class
  // and template inventory and must not travel to a third-party action URL.
  const isSameOriginTarget = url.startsWith('/') || url.includes(window.location.host);
  const fdsHeaderValue = isSameOriginTarget ? yield call(getFdsRequestHeader) : null;

  const requestHeaders: Record<string, string> = {
    ...snapshotHeaders,
    Fsc: value2 ? 'false' : silent ? 'false' : 'true',
    ...(fdsHeaderValue ? { Fds: fdsHeaderValue } : {}),
  };

  const response = yield call(fetch, url, {
    method: (method || 'POST').toUpperCase(),
    headers: requestHeaders,
    ...(body !== undefined && { body }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const responseJson = yield call([response, 'json']);
  const code = response.status;

  if (!responseJson || typeof responseJson !== 'object') {
    yield put(actionSuccess({ success: false, error: 'Invalid JSON response' }));
    if (onError?.length) {
      yield call(processEventChunks, onError, { success: false, error: 'Invalid JSON response', code }, componentId);
    }
    return;
  }

  // FE-4577 — read the design-system delivery mark BEFORE normalizing, so the
  // boundary can ingest it. `session_data.fds` in the body takes precedence and
  // is the reliable source (no `F*` header is in any `Access-Control-Expose-
  // Headers` list, so the header is stripped cross-origin); this is the fallback.
  const responseFds = response.headers.get('Fds');

  // Replaces the old `'data' in responseJson ? …` probe: same envelope-vs-flat
  // question, but it also hoists mode/live_updates/localization out of
  // `screen.settings` and rebuilds `config` from `session_data`.
  const responseData = normalizeResponseBody(responseJson, { ingest: true, fdsHeader: responseFds });
  const session_data = sessionDataTransformer(normalizeSessionData(responseJson.session_data));

  const responseEndpointId = response.headers.get('Fseid');
  const responseEndpointType = response.headers.get('Fset');
  const responseTaskRef = response.headers.get('Fref');
  const responseProcessId = response.headers.get('Fspid');

  if (responseEndpointId || responseEndpointType || responseTaskRef || responseProcessId) {
    const updatedParams: any = {};
    if (responseEndpointId) updatedParams.endpoint_id = responseEndpointId;
    if (responseEndpointType) updatedParams.endpoint_type = responseEndpointType;
    if (responseTaskRef) updatedParams.task_ref = responseTaskRef;
    if (responseProcessId) updatedParams.process_id = responseProcessId;
    yield put(updateEndpointParams(updatedParams));
  }

  if (code !== 200 || !responseData) {
    yield put(actionSuccess({ success: false, error: 'Invalid response' }));
    if (onError?.length) {
      yield call(processEventChunks, onError, { success: false, error: 'Invalid response', code, data: responseData }, componentId);
    }
    return;
  }

  // Refine <html lang> from `localization` on any successful response — a
  // process/actions response (screen: null) still carries the language even
  // though it has no screen to render, and would otherwise early-return below
  // before any parser (and its applyDocumentLanguage) runs.
  applyDocumentLanguage(responseData.localization);

  const hasScreenData = responseData && typeof responseData === 'object' &&
    (responseData.sections || responseData.screen || responseData.updates);

  if (!hasScreenData) {
    yield put(actionSuccess({ success: true, data: responseData }));
    if (onSuccess?.length) {
      yield call(processEventChunks, onSuccess, { success: true, data: responseData, code }, componentId);
    }
    return;
  }

  const { page: pp, settings: responseSettingsDraft, variableData: responseVariableData, screenId: screenIdParsed } = responseData.sections ? parseOldFormat(responseData) : parseNewFormat(responseData);

  let finalPageData = pp;
  let finalVariableData = responseVariableData;
  let responseSettings = responseSettingsDraft;
  let shouldUpdateCache = false;

  if (responseData.mode === 'update') {
    const currentPageData = yield select((state: any) => state.page.data.page);
    const currentVariableData = yield select((state: any) => state.page.variableData);
    const currentSettings = yield select((state: any) => state.page.settings);

    const { page: updatedPage, variableData: updatedVariableData, shouldUpdateCache: cacheFlag } =
      updateDataWithModeUpdate(currentPageData, responseData, currentVariableData);

    responseSettings = merge({}, currentSettings, responseSettingsDraft);
    finalPageData = updatedPage;
    finalVariableData = updatedVariableData;
    shouldUpdateCache = cacheFlag;
  }

  // Resolved once, before the render: the same address decides the scroll reset here and
  // push-vs-replace of the history entry below.
  const responseAddress = resolveCommandAddress(
    responseSettings?.command,
    merge({}, variablesForCommand || {}, finalVariableData || {}, (responseSettings?.variables || {})),
  );
  const resetScroll = !silent && !scrollResetDone && responseData.mode !== 'update'
    && isNavigationTo(responseAddress, screenIdParsed);

  yield put(pageSuccess({
    data: finalPageData,
    settings: responseSettings,
    session_data: {
      ...session_data,
      localShimmer: false,
      hash: resetScroll ? v4uuid() : `skip-${v4uuid()}`,
    },
    variableData: mergeLocalVariablesIntoGlobal(finalVariableData, responseSettings?.variables),
    screenId: screenIdParsed,
  }));

  const { status, transformed } = transformShimmerCache(responseData?.screen?.sections || []);

  if (responseData.mode === 'update') {
    if (shouldUpdateCache) {
      setLocalCache(cacheKey, {
        ...responseData,
        screen: {
          ...responseData.screen,
          sections: finalPageData,
        },
        variable_data: finalVariableData,
      });
    }
  } else if (status && transformed && !silent && responseSettings?.cache) {
      setLocalCache(cacheKey, {
        ...responseData,
        screen: {
          ...responseData.screen,
          sections: transformed,
        },
        variable_data: responseVariableData,
      });
    } else if (!silent) {
      if (cacheKey) removeFromLocalCache(cacheKey);
    }

  if (updateHistory) {
    if (responseAddress !== null && responseAddress !== currentAddress()) {
      pushNavState(`/${responseAddress}`, snapshot);
    } else {
      // No command from server, an unresolved one, or the address we are already on —
      // keep URL as-is but stamp the current entry with the new context so
      // refresh/back/forward replays this exact request.
      replaceNavState(snapshot);
    }
  }

  const formValues = parseFormValues(finalPageData);
  yield put(resetFormValuesSuccess({ data: formValues }));

  yield put(actionSuccess({ success: true, data: responseData }));
  if (onSuccess?.length) {
    yield call(processEventChunks, onSuccess, { success: true, data: responseData, code }, componentId);
  }
}

function* handleActionRequest(action: any) {
  const screenId = yield select((state) => state.page.screenId);
  const { payload, meta: localShimmer } = action || {};
  const { actionUpdate, componentId, onSuccess, onError, responseContext } = payload || {};
  const { type, method, to, toOriginal, format, data, data_TEMPORARY: dataTemporary, target, silent: legacySilent, delay: legacyDelay, settings, request_type, process_id, upload } = actionUpdate?.action || {};

  const { send_all_parameters: sendAll } = settings || {};

  // FE-4121: an action's settings all live in `action.settings` now. Old
  // definitions (not re-saved by the editor) still carry `silent`/`delay` at the
  // top level of `action` — keep honouring them, `settings` wins.
  // Resolved HERE, before `settings` is shadowed by the page settings below.
  const silent = settings?.silent ?? legacySilent;
  const actionDelay = settings?.delay ?? legacyDelay;

  // Apply delay if specified
  if (actionDelay && actionDelay > 0) {
    yield delay(actionDelay);
  }

  try {
    if (type === 'request') {
      const page = yield select((state) => state.page);
      const config = yield select((state) => state.config);
      const { settings, variableData, formValues: currentFormValues } = page || {};
      const { publicUrl, data: configData } = config || {};
      const { endpoint_id, process_id: config_process_id, endpoint_type, task_ref } = configData || {};
      const mergedVariables = merge({}, (settings?.variables || {}), variableData || {});
      // When this action is dispatched as a chained step from an upload's
      // (or any other action's) on_success/on_error, expose `$response` in
      // the placeholder bag so `{{$response.*}}` resolves inside the URL
      // and any multi-token field values that fall back to the generic
      // resolver. Field-level single-token paths still go through
      // resolveResponseContextValue first, which is unchanged.
      const variablesWithResponse = responseContext
        ? merge({}, mergedVariables, { $response: responseContext })
        : mergedVariables;
      const replacedFormValue = replacePlaceholdersWithVariables(currentFormValues, mergedVariables);

      // FE-4677: parameters are resolved BEFORE the URL is built, because an
      // endpoint path may contain `{{token}}` segments that these very parameters
      // fill. Pure motion — this block reads only `data`, `replacedFormValue`,
      // `responseContext` and `variablesWithResponse`, never the URL.
      // Transform data: extract actual values from form storage based on data keys
      let additionalData = {};
      if (data && typeof data === 'object') {
        additionalData = Object.keys(data).reduce((acc, key) => {
          const fieldConfig = data[key];
          let extractedValue;
          let targetType: string | undefined;

          // Check if there's a direct value in replacedFormValue
          if (replacedFormValue.hasOwnProperty(key)) {
            extractedValue = replacedFormValue[key];
            // If fieldConfig is an object, it might have a type
            if (typeof fieldConfig === 'object' && fieldConfig !== null && 'type' in fieldConfig) {
              targetType = fieldConfig.type;
            }
          }
          // Check if fieldConfig contains a placeholder pattern like {{variable}}
          else if (typeof fieldConfig === 'string') {
            const variableMatch = fieldConfig.match(/\{\{([^}]+)\}\}/);
            if (variableMatch) {
              const variableName = variableMatch[1].trim();
              extractedValue = resolveResponseContextValue(variableName, responseContext);
              if (extractedValue === undefined) {
                extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(fieldConfig, variablesWithResponse);
              }
            }
          }
          // Check if fieldConfig is an object with a value property
          else if (typeof fieldConfig === 'object' && fieldConfig !== null && 'value' in fieldConfig) {
            const {value} = fieldConfig;
            const {valueOriginal} = fieldConfig;
            targetType = fieldConfig.type;

            if (typeof valueOriginal === 'string' && /\{\{[^}]+\}\}/.test(valueOriginal)) {
              const variableMatch = valueOriginal.match(/\{\{([^}]+)\}\}/);
              if (variableMatch) {
                const variableName = variableMatch[1].trim();
                extractedValue = resolveResponseContextValue(variableName, responseContext);
                if (extractedValue === undefined) {
                  extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(valueOriginal, variablesWithResponse);
                }
              }
            } else if (typeof value === 'string' && /\{\{[^}]+\}\}/.test(value)) {
              const variableMatch = value.match(/\{\{([^}]+)\}\}/);
              if (variableMatch) {
                const variableName = variableMatch[1].trim();
                extractedValue = resolveResponseContextValue(variableName, responseContext);
                if (extractedValue === undefined) {
                  extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(value, variablesWithResponse);
                }
              } else {
                extractedValue = value;
              }
            } else {
              extractedValue = value;
            }
          }

          // Convert to target type if specified
          if (extractedValue !== undefined) {
            acc[key] = convertToType(extractedValue, targetType);
          }

          return acc;
        }, {} as Record<string, any>);
      }

      // Normalize publicUrl - remove leading and trailing slashes
      const normalizedPublicUrl = (publicUrl && request_type !== 'process') ? `/${publicUrl.replace(/^\/|\/$/g, '')}` : '';

      // Replace placeholders in URL
      let urlWithVariables;
      // FE-4677: path-parameter keys consumed by the URL, so they are not ALSO
      // repeated in the body / query string.
      let usedPathParamKeys: string[] = [];
      if (request_type === 'process' && process_id) {
        // For process requests, use process_external_id to construct URL
        // const processId = replacePlaceholdersWithVariables(process_external_id, mergedVariables);
        urlWithVariables = `/_processes/${process_id}`;
      } else {
        // FE-4677: an endpoint path like `/admin/loan/{{loan_ref}}` was baked to
        // `/admin/loan/` at page init — resolve from the stashed template (eventTemplate).
        const toTemplate = eventTemplate(toOriginal, to);

        // Fill `{{token}}` path segments from the action's own parameters first,
        // falling back to page variables. Tokens in the query part are left to the
        // generic resolver below, exactly as before.
        const urlVariables = withGlobalVariables(variablesWithResponse);

        const pathResult = substitutePathParams(toTemplate, additionalData, urlVariables);

        if (pathResult.missing.length) {
          // Never ship `/admin/loan/` — an empty segment can route to a DIFFERENT
          // endpoint (FE-4670). Throwing reuses the existing catch, which reports the
          // failure and runs the action's on_error chain.
          throw new Error(`Missing path parameter(s): ${pathResult.missing.join(', ')}`);
        }

        usedPathParamKeys = pathResult.used;
        urlWithVariables = replacePlaceholdersWithVariables(pathResult.path, urlVariables);
      }
      
      // Prepend publicUrl to relative URLs (not absolute URLs with protocol)
      if (normalizedPublicUrl && !urlWithVariables.match(/^https?:\/\//)) {
        if (urlWithVariables.startsWith('/')) {
          // Relative URL with leading slash: /api/endpoint
          urlWithVariables = `${normalizedPublicUrl}${urlWithVariables}`;
        } else if (urlWithVariables.length > 0) {
          // Relative URL without leading slash: api/endpoint
          urlWithVariables = `${normalizedPublicUrl}/${urlWithVariables}`;
        }
      }
      

      // Build cache action URL with all relevant request params as query params for a unique, stable cache key
      const cacheActionUrlObj = new URL(urlWithVariables, window.location.origin);
      const cacheDataParams: Record<string, any> = sendAll
        ? { ...replacedFormValue, ...additionalData }
        : { ...additionalData };
      // FE-4677: a key consumed by the path is already in the URL — drop it here so
      // the cache key matches the request we actually send. Still unique per row,
      // because the value now lives in the path itself.
      usedPathParamKeys.forEach((k) => { delete cacheDataParams[k]; });
      Object.entries(cacheDataParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          cacheActionUrlObj.searchParams.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
      const cacheActionUrl = cacheActionUrlObj.pathname + (cacheActionUrlObj.search || '');

      // Assemble the request exactly once here; the same URL/headers/body are
      // snapshotted into history.state so back/forward can replay verbatim.
      const requestData = sendAll ? {
        screen_id: screenId,
        active_route: `${window.location.pathname}${window.location.search || ''}`,
        component_id: componentId,
        device_info: getDeviceInfo(),
        session_id: SessionHelper.getOrCreateSessionId(),
        ...replacedFormValue,
        ...additionalData,
      } : {
        ...additionalData,
      };

      // FE-4677: path parameters are consumed by the URL, so they are NOT repeated
      // in the body / query string — the value appears exactly once. Done after
      // assembly so it covers the `sendAll` form-value spread too.
      // (Revert point: if the gateway does not bind path segments into the called
      // process's start input, delete this loop to send them in both places.)
      usedPathParamKeys.forEach((k) => { delete requestData[k]; });

      const requestHeaders: Record<string, string> = {
        Accept: 'application/json',
      };

      const isSameOrigin = urlWithVariables.startsWith('/') || urlWithVariables.includes(window.location.host);
      if (isSameOrigin) {
        // NOTE: Fds is deliberately NOT stamped here. These headers are frozen
        // into the `history.state` snapshot and replayed on back/forward, and a
        // design-system claim goes stale the moment anything is delivered.
        // `executeNavigationRequest` derives it at fire time instead, next to Fsc.
        requestHeaders.Fc = 'web';
        requestHeaders.Fie = 'true';

        requestHeaders.Fsi = SessionHelper.getOrCreateSessionId();
        requestHeaders.Fdi = DeviceHelper.getOrCreateDeviceId();

        if (endpoint_id) requestHeaders.Fseid = String(endpoint_id);
        if (endpoint_type) requestHeaders.Fset = String(endpoint_type);
        if (task_ref) requestHeaders.Fref = String(task_ref);
        if (screenId) requestHeaders.Fssid = String(screenId);
        if (config_process_id) requestHeaders.Fspid = String(config_process_id);
        if (componentId) requestHeaders.Fcid = String(componentId);
      }

      let finalUrl = urlWithVariables;
      let requestBody: string | undefined;

      if (method?.toUpperCase() === 'GET') {
        const urlObj = new URL(urlWithVariables, window.location.origin);
        Object.entries(requestData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            urlObj.searchParams.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
        // Keep URL relative if the original was relative, so it's portable in history.state.
        finalUrl = urlWithVariables.match(/^https?:\/\//)
          ? urlObj.toString()
          : urlObj.pathname + (urlObj.search || '');
      } else {
        const isFormFormat = format === 'form';
        if (isFormFormat) {
          requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
          const formBody = new URLSearchParams();
          Object.entries(requestData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formBody.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
            }
          });
          requestBody = formBody.toString();
        } else {
          requestHeaders['Content-Type'] = 'application/json';
          requestBody = JSON.stringify(requestData);
        }
      }

      const snapshot: NavContext = {
        kind: 'action',
        url: finalUrl,
        method: (method || 'POST').toUpperCase(),
        headers: requestHeaders,
        body: requestBody,
        componentId,
        screenId,
        cacheActionUrl,
        sendAll: !!sendAll,
      };

      yield call(executeNavigationRequest, snapshot, {
        silent,
        onSuccess,
        onError,
        responseContext,
        localShimmer,
        variablesForCommand: mergedVariables,
        updateHistory: true,
      });
    } else if (type === 'url') {
      // TNBEWEB-2022: resolved from the stashed template at event time, so variables written by
      // the event's pre functions (e.g. a token) reach the URL — `to` itself was baked at init.
      const variables = yield call(selectUrlVariables, responseContext);
      const urlWithVariables = replacePlaceholdersWithVariables(eventTemplate(toOriginal, to), variables);
      if (typeof urlWithVariables !== 'string' || !urlWithVariables) {
        // location.href = '' would reload the page; fail into on_error instead.
        throw new Error('Open url action resolved to an empty address');
      }

      if (target === '_blank') {
        // No `noopener` feature: with it window.open always returns null and hides a popup block.
        const openedWindow = window.open(urlWithVariables, '_blank');
        if (!openedWindow) {
          const errorCtx = { success: false, type: 'url', error: 'popup_blocked', url: urlWithVariables, target };
          yield put(actionSuccess({ success: false, error: errorCtx.error }));
          if (onError?.length) {
            yield call(processEventChunks, onError, errorCtx, componentId);
          }
          return;
        }
        // The opened page must not be able to navigate this one (reverse tabnabbing).
        try {
          openedWindow.opener = null;
        } catch {
          // Already cross-origin: the relationship cannot be used from there anyway.
        }
      } else {
        window.location.href = urlWithVariables;
      }

      const successCtx = { success: true, type: 'url', url: urlWithVariables, target };
      yield put(actionSuccess(successCtx));
      if (onSuccess?.length) {
        yield call(processEventChunks, onSuccess, successCtx, componentId);
      }
    } else if (type === 'upload') {
      const page = yield select((state) => state.page);
      const { settings: pageSettings, variableData, formValues: currentFormValues } = page || {};
      const mergedVariables = merge({}, (pageSettings?.variables || {}), variableData || {});
      // When this upload is itself chained from another action's
      // on_success/on_error, the upstream `responseContext` is exposed as
      // `$response` so the URL, headers, and field values can reference it.
      const variablesWithResponse = responseContext
        ? merge({}, mergedVariables, { $response: responseContext })
        : mergedVariables;
      const replacedFormValue = replacePlaceholdersWithVariables(currentFormValues, mergedVariables);

      const uploadKind = upload?.type || 'api';
      const fileSettings = upload?.file || {};
      const acceptTypes: string[] = Array.isArray(fileSettings.types) ? fileSettings.types : [];
      const multiple = !!fileSettings.multiple;
      const maxSize = typeof fileSettings.max_size === 'number' ? fileSettings.max_size : undefined;

      // Locate the single `file`-typed field in data — its key is the
      // multipart field name. Other entries in data are regular fields.
      // The editor writes both `data` (Record, canonical) and
      // `data_TEMPORARY` (raw rows). Prefer the array when present so a
      // user-added empty row round-trips, then fall back to the Record.
      let fileFieldKey: string | undefined;
      let dataEntries: Array<[string, any]> = [];
      if (Array.isArray(dataTemporary) && dataTemporary.length) {
        dataEntries = dataTemporary
          .map((row: any) => {
            if (!row || typeof row !== 'object') return null;
            const k = typeof row.key === 'string' ? row.key : '';
            if (!k) return null;
            const { key: _k, ...rest } = row as Record<string, any>;
            return [k, rest] as [string, any];
          })
          .filter((e): e is [string, any] => !!e);
      } else if (data && typeof data === 'object') {
        dataEntries = Object.entries(data);
      }
      for (const [key, cfg] of dataEntries) {
        if (cfg && typeof cfg === 'object' && (cfg as any).type === 'file') {
          fileFieldKey = key;
          break;
        }
      }

      if (!fileFieldKey) {
        const errorCtx = { error: 'Upload action requires exactly one data field with type "file"' };
        yield put(actionSuccess({ success: false, error: errorCtx.error }));
        if (onError?.length) {
          yield call(processEventChunks, onError, errorCtx, componentId);
        }
        return;
      }

      // Open native file picker and wait for the user's selection.
      const selectedFiles: File[] = yield call(pickFiles, { accept: acceptTypes, multiple });

      if (!selectedFiles.length) {
        // User cancelled — silent no-op, matches how native pickers behave.
        return;
      }

      // Validate file size (client-side; server is still source of truth).
      if (maxSize !== undefined) {
        const oversized = selectedFiles.find((f) => f.size > maxSize);
        if (oversized) {
          const errCtx = {
            error: 'File size exceeds limit',
            filename: oversized.name,
            size_bytes: oversized.size,
            max_size: maxSize,
          };
          yield put(actionSuccess({ success: false, error: errCtx.error }));
          if (onError?.length) {
            yield call(processEventChunks, onError, errCtx, componentId);
          }
          return;
        }
      }

      if (uploadKind !== 'api') {
        const errCtx = { error: `Unsupported upload type: ${uploadKind}` };
        yield put(actionSuccess({ success: false, error: errCtx.error }));
        if (onError?.length) {
          yield call(processEventChunks, onError, errCtx, componentId);
        }
        return;
      }

      const apiCfg = upload?.api || ({} as NonNullable<NonNullable<typeof upload>['api']>);
      const rawUrl = apiCfg.url || to || '';
      const uploadUrl = replacePlaceholdersWithVariables(rawUrl, variablesWithResponse);
      const finalUploadUrl = uploadUrl;
      const uploadMethod = (apiCfg.method || 'POST').toUpperCase();

      // Build user-managed headers, with placeholders resolved (e.g. so
      // `Authorization: Bearer {{token}}` works). Editor writes both
      // `headers` (Record) and `headers_TEMPORARY` (raw rows); prefer the
      // array form so partially-filled rows round-trip without dropping.
      const resolvedHeaders: Record<string, string> = {};
      const headerRows = (apiCfg as any).headers_TEMPORARY;
      if (Array.isArray(headerRows) && headerRows.length) {
        for (const row of headerRows) {
          if (!row || typeof row !== 'object') continue;
          const hKey = typeof row.key === 'string' ? row.key.trim() : '';
          if (!hKey) continue;
          const hVal = (row as any).value;
          resolvedHeaders[hKey] = typeof hVal === 'string'
            ? (replacePlaceholdersWithVariables(hVal, variablesWithResponse) as string)
            : (hVal == null ? '' : String(hVal));
        }
      } else if (apiCfg.headers && typeof apiCfg.headers === 'object') {
        for (const [hKey, hVal] of Object.entries(apiCfg.headers)) {
          if (!hKey || !hKey.trim()) continue;
          resolvedHeaders[hKey] = typeof hVal === 'string'
            ? (replacePlaceholdersWithVariables(hVal, variablesWithResponse) as string)
            : (hVal == null ? '' : String(hVal));
        }
      }

      // Build multipart/form-data: the `file` field gets the binary(s);
      // every other data entry is stringified as a regular form field.
      const formData = new FormData();
      if (multiple) {
        for (const f of selectedFiles) formData.append(fileFieldKey, f, f.name);
      } else {
        formData.append(fileFieldKey, selectedFiles[0], selectedFiles[0].name);
      }

      for (const [key, cfg] of dataEntries) {
        if (key === fileFieldKey) continue;

        let extractedValue: any;
        let targetType: string | undefined;

        if (typeof cfg === 'string') {
          extractedValue = replacePlaceholdersWithVariables(cfg, variablesWithResponse);
        } else if (cfg && typeof cfg === 'object') {
          targetType = (cfg as any).type;
          const cfgValue = (cfg as any).value;
          const cfgValueOriginal = (cfg as any).valueOriginal;

          const placeholderSource = typeof cfgValueOriginal === 'string' ? cfgValueOriginal : cfgValue;
          if (typeof placeholderSource === 'string' && /\{\{[^}]+\}\}/.test(placeholderSource)) {
            const m = placeholderSource.match(/\{\{([^}]+)\}\}/);
            if (m) {
              const variableName = m[1].trim();
              extractedValue = resolveResponseContextValue(variableName, responseContext);
              if (extractedValue === undefined) {
                extractedValue = replacedFormValue[variableName] ?? replacePlaceholdersWithVariables(placeholderSource, variablesWithResponse);
              }
            }
          } else {
            extractedValue = cfgValue;
          }
        }

        if (extractedValue === undefined || extractedValue === null) continue;
        const converted = convertToType(extractedValue, targetType);
        if (typeof converted === 'object') {
          formData.append(key, JSON.stringify(converted));
        } else {
          formData.append(key, String(converted));
        }
      }

      // Do not set Content-Type manually — the browser fills in the
      // multipart boundary automatically when a FormData body is used.
      const response = yield call(fetch, finalUploadUrl, {
        method: uploadMethod,
        headers: resolvedHeaders,
        body: formData,
      });

      const firstFile = selectedFiles[0];
      const fileMeta = multiple
        ? selectedFiles.map((f) => ({ filename: f.name, content_type: f.type, size_bytes: f.size }))
        : { filename: firstFile.name, content_type: firstFile.type, size_bytes: firstFile.size };

      let responseBody: any = null;
      try {
        const contentType = response.headers.get('Content-Type') || '';
        responseBody = contentType.includes('application/json')
          ? yield call([response, 'json'])
          : yield call([response, 'text']);
      } catch (_parseErr) {
        responseBody = null;
      }

      // Flatten the JSON body into the response context so chained chunks
      // can write `{{$response.imageUrl}}` instead of `{{$response.response.imageUrl}}`.
      // Plain-text bodies / arrays / null stay accessible via `.response`.
      const flatBody = (responseBody && typeof responseBody === 'object' && !Array.isArray(responseBody))
        ? responseBody
        : {};

      if (!response.ok) {
        const errCtx = {
          ...(multiple ? { files: fileMeta } : fileMeta),
          code: response.status,
          ...flatBody,
          response: responseBody,
          error: `Upload failed with status ${response.status}`,
        };
        yield put(actionSuccess({ success: false, error: errCtx.error }));
        if (onError?.length) {
          yield call(processEventChunks, onError, errCtx, componentId);
        }
        return;
      }

      const successCtx = multiple
        ? {
          files: fileMeta,
          code: response.status,
          ...flatBody,
          response: responseBody,
        }
        : {
          ...fileMeta,
          code: response.status,
          ...flatBody,
          response: responseBody,
        };

      // Persist upload metadata to formValues[componentId] so subsequent
      // chunks (or re-renders) can read {{componentId}} if needed.
      if (componentId) {
        yield put(formValuesSuccess({ data: { [componentId]: successCtx } }));
      }

      yield put(actionSuccess({ success: true, type: 'upload', data: successCtx }));
      if (onSuccess?.length) {
        yield call(processEventChunks, onSuccess, successCtx, componentId);
      }
    }
  } catch (error) {
    if (!silent) {
      console.error('Action request failed:', error);
    }
    yield put(actionSuccess({ success: false, error: error.message }));
    if (onError?.length) {
      yield call(processEventChunks, onError, { success: false, error: error instanceof Error ? error.message : String(error) }, componentId);
    }
  }
}

function* watchFetchRequest() {
  yield takeLatest(PageActionTypes.PAGE_REQUEST, handlePageRequest);
  yield fork(mergeStyleBatcher);
}

function* watchHandleInputRequest() {
  yield takeEvery(PageActionTypes.SET_FORM_VALUES_REQUEST, handleSetFormValuesRequest);
}

function* watchSubmitFormRequest() {
  yield takeLatest(PageActionTypes.SUBMIT_FORM_REQUEST, sendAllFormValues);
}

function* watchUpdateVariableRequest() {
  yield takeLatest(PageActionTypes.UPDATE_VARIABLE_REQUEST, updateVariableRequest);
}

function* watchActionRequest() {
  yield takeLatest(PageActionTypes.ACTION_REQUEST, handleActionRequest);
}

function* watchUncheckDependent() {
  yield takeEvery(PageActionTypes.UNCHECK_DEPENDENT_RADIO_REQUEST, handleUncheck);
}

function* pageSaga() {
  yield all([
    fork(watchHandleInputRequest),
    fork(watchFetchRequest),
    fork(watchSubmitFormRequest),
    fork(watchLocationChange),
    fork(watchUpdateVariableRequest),
    fork(watchActionRequest),
    fork(watchUncheckDependent),
  ]);
}

export default pageSaga;